// src/components/Navbar.tsx
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Collapse,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home as HomeIcon,
  Draw as DrawIcon,
  AutoAwesome as AutoAwesomeIcon,
  Rule as RuleIcon,
  Widgets as WidgetsIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Support as SupportIcon,
  AddCircle as AddCircleIcon,
  List as ListIcon,
  ExpandLess,
  ExpandMore,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { TbMessage } from "react-icons/tb";
import logo from "./../assets/Logo_Tribunal_Federal_de_Justicia.png";
import useAvailability from "../hooks/useAvailability";

// Interfaces
interface NavbarProps {
  activeItem: string;
  onItemSelect: (item: string) => void;
  role?: string;
}

interface MenuItemProps {
  text: string;
  icon: React.ReactNode;
  onClick: () => void;
  isVisible?: boolean;
  isDisabled?: boolean;
}

interface UserProfile {
  role: string;
  status: string;
}

// Utility functions
const isTokenValid = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp;
    if (!exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return exp > now;
  } catch (err) {
    console.error("Error validating token:", err);
    return false;
  }
};

const getUserProfile = (): UserProfile | null => {
  try {
    const profile = localStorage.getItem("userProfile");
    return profile ? JSON.parse(profile) : null;
  } catch (error) {
    console.error("Error parsing userProfile from localStorage:", error);
    return null;
  }
};

const getUserPermissions = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem("userPermissions") || "[]");
  } catch (error) {
    console.error("Error parsing userPermissions from localStorage:", error);
    return [];
  }
};

// Constants
const DRAWER_WIDTH = "20%";
const ROUTES = {
  HOME: "/dashboard",
  USER_MANAGEMENT: "/userManagement",
  COMMENTS: "/comments",
  PRECEDENT_ANALYSIS: "/listPrecedent",
  NEW_TRANSCRIPTION: "/newTranscriptor",
  MY_TRANSCRIPTIONS: "/listTranscriptor",
  ALL_TRANSCRIPTIONS: "/allTranscriptor",
  GENAI_SEARCH: "/genAiSearch",
  GENAI_PROMPT: "/genAiPrompt",
  PROFILE: "/profileEmployer",
  SUPPORT: "/faqPage",
  SESSION_CLOSED: "/session-closed",
  LOGIN: "/",
} as const;

const COLORS = {
  PRIMARY: "#002E74",
  DANGER: "#D32F2F",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#AAAAAA",
  TEXT_DISABLED: "#B0BEC5",
} as const;

const Navbar: React.FC<NavbarProps> = ({ activeItem, onItemSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [openTranscriptor, setOpenTranscriptor] = useState(false);
  const [openInteligencia, setOpenInteligencia] = useState(false);

  // Memoized data
  const userProfile = useMemo(() => getUserProfile(), []);
  const userPermissions = useMemo(() => getUserPermissions(), []);
  const availability = useAvailability(userProfile, userPermissions);

  const isAdmin = useMemo(() => userProfile?.role === "admin", [userProfile]);
  const isSupervisor = useMemo(() => userProfile?.role === "Supervisor", [userProfile]);
  const isAwaitingApproval = useMemo(
    () => userProfile?.status === "AWAITING_APPROVAL",
    [userProfile]
  );

  // Path checks
  const isTranscriptorPath = useMemo(
    () =>
      location.pathname.startsWith("/newTranscriptor") ||
      location.pathname.startsWith("/listTranscriptor") ||
      location.pathname.startsWith("/allTranscriptor"),
    [location.pathname]
  );

  const isInteligenciaPath = useMemo(
    () =>
      location.pathname.startsWith("/genAiSearch") ||
      location.pathname.startsWith("/genAiPrompt"),
    [location.pathname]
  );

  // Handlers
  const handleLogout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    navigate(ROUTES.LOGIN);
  }, [navigate]);

  const handleMenuToggle = useCallback(
    (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
      setter((prev) => !prev);
    },
    []
  );

  const getActiveItem = useCallback(() => {
    const path = location.pathname;
    
    // Route mapping for cleaner code
    const routeMap: Record<string, string> = {
      "/userManagement": "Administración de Usuarios",
      "/listPrecedent": "Análisis de Precedentes",
      "/newTranscriptor": "Nueva Transcripción",
      "/listTranscriptor": "Mis Transcripciones",
      "/allTranscriptor": "Todas las Transcripciones",
      "/genAiSearch": "Búsqueda GenAi",
      "/comments": "Comentarios",
      "/genAiPrompt": "Prompt GenAi",
      "/profileEmployer": "Perfil",
      "/faqPage": "Soporte",
      "/dashboard": "Home",
    };

    // Check for exact matches first
    if (routeMap[path]) {
      return routeMap[path];
    }

    // Check for path prefixes
    for (const [route, title] of Object.entries(routeMap)) {
      if (path.startsWith(route)) {
        return title;
      }
    }

    return "";
  }, [location.pathname]);

  // Token validation effect
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token || !isTokenValid(token)) {
      navigate(ROUTES.SESSION_CLOSED);
      return;
    }

    const item = getActiveItem();
    if (item) {
      onItemSelect(item);
    }
  }, [location.pathname, getActiveItem, onItemSelect, navigate]);

  // Styles
  const getItemStyle = useCallback(
    (itemName: string, isDisabled: boolean) => ({
      backgroundColor:
        (activeItem || getActiveItem()) === itemName && !isDisabled
          ? COLORS.PRIMARY
          : "inherit",
      color: isDisabled
        ? COLORS.TEXT_DISABLED
        : (activeItem || getActiveItem()) === itemName
        ? COLORS.TEXT_PRIMARY
        : COLORS.TEXT_SECONDARY,
      fontFamily: "Montserrat, sans-serif",
      fontSize: "14px",
      borderRadius: "4px",
      margin: "8px 0",
      "&:hover": {
        backgroundColor: !isDisabled ? COLORS.PRIMARY : "inherit",
        color: !isDisabled ? COLORS.TEXT_PRIMARY : COLORS.TEXT_DISABLED,
      },
      border: "none",
    }),
    [activeItem, getActiveItem]
  );

  const listItemTextStyle = useCallback(
    (itemName: string, isDisabled: boolean) => ({
      fontSize: "14px",
      color: isDisabled
        ? COLORS.TEXT_DISABLED
        : (activeItem || getActiveItem()) === itemName
        ? COLORS.TEXT_PRIMARY
        : COLORS.TEXT_SECONDARY,
      fontFamily: "Montserrat, sans-serif",
    }),
    [activeItem, getActiveItem]
  );

  const listItemIconStyle = useMemo(
    () => ({
      minWidth: "35px",
      color: COLORS.TEXT_SECONDARY,
    }),
    []
  );

  const salirButtonStyle = useMemo(
    () => ({
      backgroundColor:
        (activeItem || getActiveItem()) === "Salir" ? COLORS.DANGER : "inherit",
      color: COLORS.DANGER,
      borderRadius: "4px",
      "&:hover": {
        color: COLORS.TEXT_PRIMARY,
      },
    }),
    [activeItem, getActiveItem]
  );

  // Funciones helper para reducir complejidad cognitiva
  const getMenuItemStyle = useCallback(
    (text: string, isDisabled: boolean, isSalir: boolean) => {
      if (isSalir) {
        const currentActiveItem = activeItem || getActiveItem();
        return {
          ...salirButtonStyle,
          backgroundColor: currentActiveItem === "Salir" ? COLORS.DANGER : "inherit",
        };
      }
      return getItemStyle(text, isDisabled);
    },
    [activeItem, getActiveItem, salirButtonStyle, getItemStyle]
  );

  const getMenuItemColor = useCallback(
    (text: string, isDisabled: boolean, isSalir: boolean) => {
      if (isSalir) return COLORS.DANGER;
      if (isDisabled) return COLORS.TEXT_DISABLED;
      
      const currentActiveItem = activeItem || getActiveItem();
      return currentActiveItem === text ? COLORS.TEXT_PRIMARY : COLORS.TEXT_SECONDARY;
    },
    [activeItem, getActiveItem]
  );

  const handleMenuItemClick = useCallback(
    (onClick: () => void, text: string, isDisabled: boolean) => {
      if (!isDisabled) {
        onClick();
        onItemSelect(text);
      }
    },
    [onItemSelect]
  );

  // MenuItem Component
  const MenuItem = useCallback(
    ({
      text,
      icon,
      onClick,
      isVisible = true,
      isDisabled = false,
    }: MenuItemProps): JSX.Element | null => {
      if (!isVisible) return null;

      const isSalir = text === "Salir";
      const itemStyle = getMenuItemStyle(text, isDisabled, isSalir);
      const iconColor = getMenuItemColor(text, isDisabled, isSalir);
      const textColor = getMenuItemColor(text, isDisabled, isSalir);

      return (
        <ListItem
          button
          sx={itemStyle}
          onClick={() => handleMenuItemClick(onClick, text, isDisabled)}
        >
          <ListItemIcon
            sx={{
              color: iconColor,
              ...listItemIconStyle,
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={text}
            sx={{
              fontSize: "14px",
              color: textColor,
              fontFamily: "Montserrat, sans-serif",
            }}
          />
        </ListItem>
      );
    },
    [
      getMenuItemStyle,
      getMenuItemColor,
      handleMenuItemClick,
      listItemIconStyle,
    ]
  );

  // Transcriptor Section Component
  const TranscriptorSection = useCallback(() => {
    const shouldShow = (isAdmin || isSupervisor) && availability.transcriptor !== false;

    if (!shouldShow) return null;

    return (
      <>
        <ListItem
          button
          sx={
            isTranscriptorPath
              ? {
                  ...getItemStyle("Todas las transcripciones", false),
                  backgroundColor: COLORS.PRIMARY,
                  color: COLORS.TEXT_PRIMARY,
                }
              : getItemStyle("Todas las transcripciones", false)
          }
          onClick={() => navigate(ROUTES.ALL_TRANSCRIPTIONS)}
        >
          <ListItemIcon
            sx={{
              ...listItemIconStyle,
              color: isTranscriptorPath
                ? COLORS.TEXT_PRIMARY
                : COLORS.TEXT_SECONDARY,
            }}
          >
            <DrawIcon />
          </ListItemIcon>
          <ListItemText
            primary="Todas las transcripciones"
            sx={listItemTextStyle("Todas las transcripciones", false)}
          />
        </ListItem>

        <Collapse in={openTranscriptor} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItem
              text="Nueva Transcripción"
              icon={<AddCircleIcon />}
              onClick={() => navigate(ROUTES.NEW_TRANSCRIPTION)}
            />
            {(isAdmin || isSupervisor) ? (
              <MenuItem
                text="Todas las Transcripciones"
                icon={<AssignmentIcon />}
                onClick={() => navigate(ROUTES.ALL_TRANSCRIPTIONS)}
              />
            ) : (
              <MenuItem
                text="Mis Transcripciones"
                icon={<ListIcon />}
                onClick={() => navigate(ROUTES.MY_TRANSCRIPTIONS)}
              />
            )}
          </List>
        </Collapse>
      </>
    );
  }, [
    isAdmin,
    isSupervisor,
    availability.transcriptor,
    isTranscriptorPath,
    getItemStyle,
    listItemIconStyle,
    listItemTextStyle,
    handleMenuToggle,
    openTranscriptor,
    navigate,
    MenuItem,
  ]);

  // Inteligencia Documental Section Component
  const InteligenciaSection = useCallback(() => {
    if (!availability.inteligenciaDocumental) return null;

    return (
      <>
        <ListItem
          button
          sx={
            isInteligenciaPath
              ? {
                  ...getItemStyle("Inteligencia Documental", false),
                  backgroundColor: COLORS.PRIMARY,
                  color: COLORS.TEXT_PRIMARY,
                }
              : getItemStyle("Inteligencia Documental", false)
          }
          onClick={() => handleMenuToggle(setOpenInteligencia)}
        >
          <ListItemIcon
            sx={{
              ...listItemIconStyle,
              color: isInteligenciaPath
                ? COLORS.TEXT_PRIMARY
                : COLORS.TEXT_SECONDARY,
            }}
          >
            <AutoAwesomeIcon />
          </ListItemIcon>
          <ListItemText
            primary="Inteligencia Documental"
            sx={listItemTextStyle("Inteligencia Documental", false)}
          />
          {openInteligencia ? <ExpandLess /> : <ExpandMore />}
        </ListItem>

        <Collapse in={openInteligencia} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <MenuItem
              text="Prompt GenAi"
              icon={<WidgetsIcon />}
              onClick={() => navigate(ROUTES.GENAI_PROMPT)}
              isVisible={availability.promptGenAi}
            />
          </List>
        </Collapse>
      </>
    );
  }, [
    availability.inteligenciaDocumental,
    availability.promptGenAi,
    isInteligenciaPath,
    getItemStyle,
    listItemIconStyle,
    listItemTextStyle,
    handleMenuToggle,
    openInteligencia,
    navigate,
    MenuItem,
  ]);

 

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          paddingTop: "60px",
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 20px 10px",
          }}
        >
          <img
            src={logo}
            alt="Logo Tribunal Federal de Justicia"
            style={{
              width: "220px",
              height: "194.22px",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Navigation Menu */}
        <List sx={{ listStyle: "none", margin: "16px", padding: 0 }}>
          {/* Home */}
          <MenuItem
            text="Home"
            icon={<HomeIcon />}
            onClick={() => navigate(ROUTES.HOME)}
            isDisabled={isAwaitingApproval}
          />

          {/* Main Menu Items (only if not awaiting approval) */}
          {!isAwaitingApproval && (
            <>
              {/* Admin Items */}
              {isAdmin && (
                <>
                  <MenuItem
                    text="Administración de Usuarios"
                    icon={<AdminPanelSettingsIcon />}
                    onClick={() => navigate(ROUTES.USER_MANAGEMENT)}
                  />
                </>
              )}

              {/* Comentarios - Visible para Admin y Supervisor */}
              {(isAdmin || isSupervisor) && (
                <MenuItem
                  text="Comentarios"
                  icon={
                    <TbMessage
                      size={24}
                      strokeWidth={1.5}
                    />
                  }
                  onClick={() => navigate(ROUTES.COMMENTS)}
                />
              )}

              {/* Precedent Analysis */}
              <MenuItem
                text="Análisis de Precedentes"
                icon={<RuleIcon />}
                onClick={() => navigate(ROUTES.PRECEDENT_ANALYSIS)}
                isVisible={availability.analisisDePrecedente}
              />

              {/* Transcriptor Section */}
              <TranscriptorSection />

              {/* Inteligencia Documental Section */}
              <InteligenciaSection />
            </>
          )}
        </List>
      </Box>

      {/* User Section - Bottom */}
      {availability.perfil && (
        <Box sx={{ padding: "16px" }}>
          <Box sx={{ padding: "8px 0" }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: COLORS.TEXT_SECONDARY,
                paddingLeft: "16px",
                fontSize: "14px",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              USUARIO
            </Typography>
          </Box>

          <List sx={{ padding: 0 }}>
            <MenuItem
              text="Perfil"
              icon={<AccountCircleIcon />}
              onClick={() => navigate(ROUTES.PROFILE)}
            />

            <MenuItem
              text="Soporte"
              icon={<SupportIcon />}
              onClick={() => navigate(ROUTES.SUPPORT)}
              isVisible={availability.soporte}
            />

            <MenuItem
              text="Salir"
              icon={<ExitToAppIcon />}
              onClick={handleLogout}
            />
          </List>
        </Box>
      )}
    </Drawer>
  );
};

export default Navbar;